/**
 * LoginPage Component
 *
 * Handles user authentication with multiple OAuth providers (LinkedIn, GitHub, Google)
 * and email OTP verification. Uses Cloudflare Turnstile for CAPTCHA protection.
 *
 * @module components/auth/LoginPage
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import confetti from 'canvas-confetti';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { getEnv } from '@/lib/utils/env';
import { VerificationCodeInput } from './VerificationCodeInput';
import { LinkedInFilledIcon } from '../icons/LinkedInFilledIcon';
import { GitHubIcon } from '../icons/GitHubIcon';
import { GoogleIcon } from '../icons/GoogleIcon';
import { EmailIcon } from '../icons/EmailIcon';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { ButtonContent } from '../ui/ButtonContent';
import { AuthProductPreview } from './AuthProductPreview';
import './LoginPage.css';

// ============================================================================
// TYPES
// ============================================================================

/** Authentication flow step */
type AuthStep = 'initialEmail' | 'verification';

/** Supported OAuth providers */
type OAuthProvider = 'linkedin_oidc' | 'github' | 'google';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Resend cooldown period in seconds */
const RESEND_COOLDOWN_SECONDS = 120;

/**
 * Allow-list regex for the `next` query-string param. Prevents open-redirect
 * attacks by only accepting paths that match a known internal route family:
 *   - /invite/{token}      (token is alphanumeric)
 *   - /admin               (root admin page)
 *   - /admin/...           (admin subpages + optional query string)
 *   - /oauth/consent       (OAuth consent screen, with its authorization_id
 *                           query string, so the consent flow survives a login
 *                           bounce)
 */
const NEXT_PARAM_ALLOWLIST = /^\/(invite\/[a-zA-Z0-9]+|admin(\/.*)?|oauth\/consent(\?.*)?)$/;

/**
 * Validate a candidate `next` path. Returns the path if safe to navigate to,
 * otherwise returns `/admin`.
 */
function resolveNextTarget(candidate: string | null): string {
  if (!candidate) return '/admin';
  return NEXT_PARAM_ALLOWLIST.test(candidate) ? candidate : '/admin';
}

// ============================================================================
// STYLES
// ============================================================================

const OAUTH_BUTTON_CLASSES =
  'w-full border border-border bg-surface text-foreground hover:border-border hover:bg-surface-hover hover:text-foreground py-3 px-4 flex items-center justify-center gap-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

const INPUT_CLASSES =
  'w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-subtle-foreground focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt transition-colors';

const SUBMIT_BUTTON_CLASSES =
  'w-full py-3 px-4 button-accent rounded-lg transition-all duration-200 disabled:bg-surface-raised disabled:cursor-not-allowed disabled:text-muted-foreground flex items-center justify-center gap-3';

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Login page component with OAuth and email OTP authentication.
 *
 * Provides multiple authentication methods:
 * - OAuth sign-in with LinkedIn, GitHub, and Google
 * - Email OTP (one-time password) verification
 *
 * Email auth is gated by an **invisible Cloudflare Turnstile** widget
 * running in proactive mode (`execution: 'render'`) - the bot challenge is
 * solved silently in the background at mount, so the user never sees a
 * CAPTCHA UI. At submit time we simply read the pre-solved token via
 * `getResponsePromise()` and forward it to Supabase as `captchaToken`.
 */
export const LoginPage: React.FC = () => {
  const { supabase, session } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  /**
   * Resolved post-login redirect target. `?next=` wins over
   * location.state.from so invite deep-links survive OAuth round-trips.
   */
  const nextTarget = useMemo(() => {
    const raw = searchParams.get('next');
    if (raw) return resolveNextTarget(raw);
    const stateFrom = (location.state as { from?: string } | null)?.from;
    return resolveNextTarget(stateFrom ?? null);
  }, [searchParams, location.state]);

  // Auth state
  const [step, setStep] = useState<AuthStep>('initialEmail');
  const [loading, setLoading] = useState(false);
  const [oauthProvider, setOAuthProvider] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const previousStep = useRef(step);

  useEffect(() => {
    if (step === 'initialEmail' && previousStep.current === 'verification') emailInputRef.current?.focus();
    previousStep.current = step;
  }, [step]);

  // Resend timer state
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Turnstile CAPTCHA reference
  const turnstileRef = useRef<TurnstileInstance>(null);

  /**
   * Post-demo signup variant - opt-in via `?promo=demo`. Used by the demo
   * exit redirect to celebrate (confetti) and swap the header copy to invite
   * the user to claim their 14-day pro trial.
   */
  const isPromoDemo = searchParams.get('promo') === 'demo';
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Stable options for the Turnstile widget.
   *
   * Memoized so the props reference never changes across re-renders. The
   * underlying `@marsidev/react-turnstile` wrapper tears the widget down and
   * re-renders it whenever its internal params memo invalidates - and
   * Cloudflare's runtime then rejects any subsequent token retrieval with
   * "parameters … not allowed to be changed between calls of render() and
   * execute()". Keeping this object stable eliminates that failure mode.
   *
   * `execution: 'render'` means the invisible challenge is solved
   * proactively at mount, so token retrieval at submit time is a no-op
   * `getResponsePromise()` lookup rather than a fragile `execute()` call.
   */
  const turnstileOptions = useMemo(
    () =>
      ({
        theme: 'dark',
        size: 'invisible',
        execution: 'render',
      }) as const,
    [],
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Redirect authenticated users away from the login page.
   */
  useEffect(() => {
    if (session) {
      captureEvent('auth_already_logged_in_redirect', { redirect_to: nextTarget });
      void navigate(nextTarget, { replace: true });
    }
  }, [session, navigate, nextTarget]);

  /**
   * Fire a one-shot confetti burst on mount when the promo variant is active.
   * The post-demo redirect lands here, so the burst happens immediately.
   */
  useEffect(() => {
    if (!isPromoDemo) return;
    if (!confettiCanvasRef.current) return;

    try {
      const fire = confetti.create(confettiCanvasRef.current, { resize: true });
      void fire({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#7c3aed', '#a78bfa', '#ffffff'],
      });
      captureEvent('auth_promo_demo_landed');
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'LoginPage.promoConfetti',
      );
    }
  }, [isPromoDemo]);

  /**
   * Countdown timer for resend button cooldown.
   */
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  // ============================================================================
  // STATE TRANSITIONS
  // ============================================================================

  /**
   * Starts the resend cooldown timer.
   */
  const startResendTimer = useCallback(() => {
    setResendDisabled(true);
    setResendTimer(RESEND_COOLDOWN_SECONDS);
  }, []);

  /**
   * Transitions to a new authentication step with state cleanup.
   */
  const transitionToStep = useCallback(
    (newStep: AuthStep) => {
      setStep(newStep);
      setLoading(false);
      setError(null);

      if (newStep === 'verification') {
        startResendTimer();
        captureEvent('auth_verification_step_entered', { email_domain: email.split('@')[1] });
      } else if (newStep === 'initialEmail') {
        turnstileRef.current?.reset();
      }
    },
    [email, startResendTimer]
  );

  // ============================================================================
  // EMAIL OTP HANDLERS
  // ============================================================================

  /**
   * Handles email form submission for OTP authentication.
   *
   * The Turnstile widget runs proactively at mount (`execution: 'render'`),
   * so a token is already being solved in the background by the time the
   * user submits. `getResponsePromise()` waits for that token without ever
   * calling Cloudflare's `execute()` - which avoids the param-equality
   * failure path some users hit due to race conditions or extensions.
   */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setOAuthProvider(null);
    setLoading(true);
    setError(null);
    captureEvent('auth_email_submit_started', { email_domain: email.split('@')[1] });

    try {
      const turnstile = turnstileRef.current;
      if (!turnstile) {
        throw new Error('Verification not ready. Please try again in a moment.');
      }

      const captchaToken = await turnstile.getResponsePromise();

      const { error: signInError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          captchaToken,
          shouldCreateUser: true,
        },
      });

      if (signInError) throw signInError;

      // Token is single-use - reset so the widget solves a fresh challenge
      // in case the user comes back to this step.
      turnstileRef.current?.reset();
      captureEvent('auth_email_otp_sent', { email_domain: email.split('@')[1] });
      transitionToStep('verification');
    } catch (err) {
      turnstileRef.current?.reset();
      setLoading(false);

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send verification code. Please try again.';
      setError(errorMessage);

      captureException(
        err instanceof Error ? err : new Error(errorMessage),
        'LoginPage.handleEmailSubmit',
        { email_domain: email.split('@')[1] }
      );
    }
  };

  /**
   * Handles resending the verification code. Resets the Turnstile widget
   * first so a fresh challenge is solved before requesting a new OTP.
   */
  const handleResendVerification = async () => {
    if (resendDisabled) return;

    setLoading(true);
    setError(null);
    captureEvent('auth_resend_code_started', { email_domain: email.split('@')[1] });

    try {
      const turnstile = turnstileRef.current;
      if (!turnstile) {
        throw new Error('Verification not ready. Please try again in a moment.');
      }

      turnstile.reset();
      const captchaToken = await turnstile.getResponsePromise();

      const { error: resendError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          captchaToken,
          shouldCreateUser: false,
        },
      });

      if (resendError) throw resendError;

      turnstileRef.current?.reset();
      startResendTimer();
      captureEvent('auth_resend_code_success', { email_domain: email.split('@')[1] });
    } catch (err) {
      turnstileRef.current?.reset();

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to resend verification code';
      setError(errorMessage);

      captureException(
        err instanceof Error ? err : new Error(errorMessage),
        'LoginPage.handleResendVerification',
        { email_domain: email.split('@')[1] }
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // VERIFICATION HANDLERS
  // ============================================================================

  /**
   * Handles successful OTP verification.
   */
  const handleVerificationSuccess = useCallback(() => {
    captureEvent('auth_verification_success', { redirect_to: nextTarget });
    void navigate(nextTarget, { replace: true });
  }, [nextTarget, navigate]);

  /**
   * Handles verification error from the VerificationCodeInput component.
   */
  const handleVerificationError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    captureException(new Error(errorMessage), 'LoginPage.handleVerificationError', {
      step: 'verification',
    });
  }, []);

  /**
   * Returns to the email input step.
   */
  const handleBackToEmail = useCallback(() => {
    captureEvent('auth_back_to_email', { from_step: step });
    transitionToStep('initialEmail');
  }, [step, transitionToStep]);

  // ============================================================================
  // OAUTH HANDLERS
  // ============================================================================

  /**
   * Handles OAuth sign-in with the specified provider.
   * Configures provider-specific options and redirects to OAuth flow.
   */
  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setOAuthProvider(provider);
    setLoading(true);
    setError(null);
    captureEvent('auth_oauth_started', { provider });

    try {
      const redirectTo = `${window.location.origin}${nextTarget}`;

      const options: { redirectTo: string; queryParams?: Record<string, string> } = {
        redirectTo,
      };

      // Configure provider-specific OAuth parameters
      if (provider === 'linkedin_oidc') {
        options.queryParams = {
          access_type: 'offline',
          prompt: 'consent',
          scope: 'openid email profile',
        };
      } else if (provider === 'google') {
        options.queryParams = {
          scope: 'openid email profile',
          access_type: 'offline',
          prompt: 'consent',
        };
      }

      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options,
      });

      if (oauthError) throw oauthError;
      if (!data.url) throw new Error('No OAuth URL returned from Supabase');

      captureEvent('auth_oauth_redirect', { provider });
      window.location.href = data.url;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to sign in with ${provider}`;
      setError(errorMessage);
      setLoading(false);

      captureException(
        err instanceof Error ? err : new Error(errorMessage),
        'LoginPage.handleOAuthSignIn',
        { provider }
      );
    }
  };

  // ============================================================================
  // TURNSTILE ERROR HANDLER
  // ============================================================================

  /**
   * Handles Turnstile CAPTCHA errors.
   */
  const handleTurnstileError = useCallback(() => {
    const error = new Error('Verification failed. Please try again.');
    setError(error.message);
    captureException(error, 'LoginPage.turnstileError');
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <main className="auth-page bg-background">
      {isPromoDemo && (
        <canvas
          ref={confettiCanvasRef}
          className="fixed inset-0 w-screen h-screen pointer-events-none"
          style={{ zIndex: 5 }}
        />
      )}
      <div className="auth-layout">
        <div className="auth-card glass-card p-6 md:p-8" data-theme="light">
          {/* Header */}
          <div className="mb-8">
            <div className="flex mb-6">
              <img
                src="/holito-logo-black.svg"
                alt="holito"
                className="h-8 w-auto"
              />
            </div>
            <p className={step === 'initialEmail' && isPromoDemo ? 'text-lg text-foreground' : 'text-muted-foreground'}>
              <AnimatedLabel multiline text={step === 'initialEmail' && isPromoDemo
                ? 'Next, build your own. Create an account now and unlock full Pro features free for 14 days.'
                : step === 'verification' ? 'Enter verification code' : 'Sign in or create an account'} />
            </p>
          </div>

          {/* Error Message */}
          <AnimatedPresence show={Boolean(error)}>
            <div className="pb-6">
              <div role="alert" className="p-4 text-sm text-foreground bg-wash-danger rounded-lg">
                <AnimatedLabel multiline text={error ?? ''} />
              </div>
            </div>
          </AnimatedPresence>

          {/* Verification Step */}
          <AnimatedPresence show={step === 'verification'}>
            <VerificationCodeInput
              email={email}
              onResend={handleResendVerification}
              loading={loading}
              resendDisabled={resendDisabled}
              resendTimer={resendTimer}
              submitButtonText="Continue"
              onVerificationSuccess={handleVerificationSuccess}
              onVerificationError={handleVerificationError}
            />

            <div className="mt-4 text-center">
              <button
                onClick={handleBackToEmail}
                className="text-sm text-volt-soft hover:text-volt-soft"
                disabled={loading}
              >
                &larr; Back to email
              </button>
            </div>
          </AnimatedPresence>

          {/* Initial Email Step */}
          <AnimatedPresence show={step === 'initialEmail'}>
            {/* OAuth Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => handleOAuthSignIn('linkedin_oidc')}
                disabled={loading}
                aria-busy={loading && oauthProvider === 'linkedin_oidc'}
                className={OAUTH_BUTTON_CLASSES}
              >
                <ButtonContent loading={loading && oauthProvider === 'linkedin_oidc'}>
                  <LinkedInFilledIcon className="w-5 h-5" />
                  <span>Continue with LinkedIn</span>
                </ButtonContent>
              </button>

              <button
                onClick={() => handleOAuthSignIn('github')}
                disabled={loading}
                aria-busy={loading && oauthProvider === 'github'}
                className={OAUTH_BUTTON_CLASSES}
              >
                <ButtonContent loading={loading && oauthProvider === 'github'}>
                  <GitHubIcon className="w-5 h-5" />
                  <span>Continue with GitHub</span>
                </ButtonContent>
              </button>

              <button
                onClick={() => handleOAuthSignIn('google')}
                disabled={loading}
                aria-busy={loading && oauthProvider === 'google'}
                className={OAUTH_BUTTON_CLASSES}
              >
                <ButtonContent loading={loading && oauthProvider === 'google'}>
                  <GoogleIcon className="w-5 h-5" />
                  <span>Continue with Google</span>
                </ButtonContent>
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} aria-busy={loading && !oauthProvider} className="space-y-4">
              <input
                ref={emailInputRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={INPUT_CLASSES}
                required
                disabled={loading}
              />

              <button type="submit" disabled={loading || !email} aria-busy={loading && !oauthProvider} className={SUBMIT_BUTTON_CLASSES}>
                <ButtonContent loading={loading && !oauthProvider}>
                  <EmailIcon aria-hidden="true" className="size-5 shrink-0" />
                  Continue with Email
                </ButtonContent>
              </button>
            </form>
          </AnimatedPresence>

          {/* Footer */}
          <p className="mt-8 text-sm text-muted-foreground">
            By signing in, you agree to our{' '}
            <a href="https://holito.app/terms/" target="_blank" rel="noopener noreferrer" className="text-volt-soft hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="https://holito.app/privacy/" target="_blank" rel="noopener noreferrer" className="text-volt-soft hover:underline">Privacy Policy</a>.
          </p>
        </div>
        <AuthProductPreview />
      </div>

      {/* Invisible Turnstile CAPTCHA - proactive `render` mode so the
          challenge is solved silently in the background at mount. */}
      <Turnstile
        ref={turnstileRef}
        siteKey={getEnv('VITE_TURNSTILE_SITE_KEY', false, '')!}
        options={turnstileOptions}
        onError={handleTurnstileError}
      />
    </main>
  );
};

export default LoginPage;
